import React, { useCallback, useEffect, useState } from 'react';
import { auditLogApi, AuditLogListItem, AuditLogQueryParams } from '../api/auditLog';
import { getErrorMessage } from '../api/client';
import { notifyError } from '../utils/notify';
import { ChevronDown, ChevronRight, ClipboardList } from 'lucide-react';
import EnglishDatePicker from '../components/common/EnglishDatePicker';

const ACTOR_LABEL: Record<string, string> = {
  ADMIN: 'Admin',
  CLIENT: 'Client',
  SYSTEM: 'System',
};

function formatInstant(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'medium',
    });
  } catch {
    return iso;
  }
}

function PreviewText({ label, text }: { label: string; text: string | null | undefined }) {
  if (text === null || text === undefined || text === '') return null;
  const max = 280;
  const clipped = text.length > max ? `${text.slice(0, max)}…` : text;
  return (
    <div className="mt-1 text-xs">
      <span className="font-semibold text-gray-600">{label}: </span>
      <span className="font-mono text-gray-800 whitespace-pre-wrap break-all">{clipped}</span>
    </div>
  );
}

function RowDetailPanel({ row }: { row: AuditLogListItem }) {
  return (
    <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-sm space-y-2">
      <p className="text-gray-700">
        <span className="font-semibold text-gray-600">Description: </span>
        {row.description || '—'}
      </p>
      {row.userAgent ? (
        <p className="text-xs text-gray-500">
          <span className="font-semibold">User-Agent: </span>
          <span className="break-all">{row.userAgent}</span>
        </p>
      ) : null}
      {(row.oldValue || row.newValue) && (
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">old_value</p>
            <pre className="max-h-40 overflow-auto rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-800 whitespace-pre-wrap break-all">
              {row.oldValue ?? '∅'}
            </pre>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">new_value</p>
            <pre className="max-h-40 overflow-auto rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-800 whitespace-pre-wrap break-all">
              {row.newValue ?? '∅'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AuditLogPage() {
  const [rows, setRows] = useState<AuditLogListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const [filters, setFilters] = useState<AuditLogQueryParams>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const payload = await auditLogApi.list({
        ...filters,
        page,
        pageSize,
      });
      const data = payload?.data;
      if (!payload || payload.isSuccess === false || !data) {
        const err = typeof payload?.error === 'string' ? payload.error : getErrorMessage(null, 'Failed to load audit log');
        notifyError(null, err);
        setRows([]);
        setTotalCount(0);
        return;
      }
      setRows(Array.isArray(data.items) ? data.items : []);
      setTotalCount(typeof data.totalCount === 'number' ? data.totalCount : 0);
    } catch (e: unknown) {
      notifyError(e, getErrorMessage(e, 'Failed to load audit log'));
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <ClipboardList className="text-indigo-600" size={26} aria-hidden />
            Audit Log
          </h1>
          <p className="mt-1 text-gray-600 max-w-3xl">
            Immutable trail of financially significant back-office actions (approvals, status changes, internal transfers).
            Rows map to{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">audit_log</code>: entity_type, entity_id, action,
            actor, timestamp, ip_address, snapshots.
          </p>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            Search
            <input
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Action, entity, description…"
              value={filters.search ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value || undefined }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            Entity type
            <input
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="OUTGOING_PAYMENT"
              value={filters.entityType ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, entityType: e.target.value || undefined }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            Entity id (UUID substring)
            <input
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
              placeholder="payment guid"
              value={filters.entityId ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, entityId: e.target.value || undefined }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            Action contains
            <input
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="APPROVE_PAYMENT"
              value={filters.action ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value || undefined }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            Actor type
            <select
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
              value={filters.actorType ?? ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  actorType: e.target.value ? e.target.value : undefined,
                }))
              }
            >
              <option value="">Any</option>
              <option value="ADMIN">ADMIN</option>
              <option value="CLIENT">CLIENT</option>
              <option value="SYSTEM">SYSTEM</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            From (UTC date)
            <EnglishDatePicker
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              value={filters.fromUtc?.slice(0, 10) ?? ''}
              onChange={(value) =>
                setFilters((f) => ({
                  ...f,
                  fromUtc: value ? `${value}T00:00:00Z` : undefined,
                }))
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            To (UTC date)
            <EnglishDatePicker
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              value={filters.toUtc?.slice(0, 10) ?? ''}
              onChange={(value) =>
                setFilters((f) => ({
                  ...f,
                  toUtc: value ? `${value}T23:59:59Z` : undefined,
                }))
              }
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={() => {
              setPage(1);
              void load();
            }}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Apply filters
          </button>
          <button
            type="button"
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>

          <div className="ml-auto flex items-center gap-2 text-sm text-gray-600">
            <span>Page size</span>
            <select
              className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm"
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
            >
              {[25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-gray-500">No audit entries match the current filters.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {/* expand */}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Time (UTC-ish)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Actor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Action
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Entity
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      IP
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Delta
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {rows.map((row) => {
                    const isOpen = expanded.has(row.id);
                    return (
                      <React.Fragment key={row.id}>
                        <tr className="hover:bg-gray-50 transition-colors align-top">
                          <td className="px-2 py-3 w-10">
                            <button
                              type="button"
                              aria-expanded={isOpen}
                              aria-label={isOpen ? 'Collapse row' : 'Expand row'}
                              className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                              onClick={() => toggleExpand(row.id)}
                            >
                              {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                            {formatInstant(row.timestamp)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                row.actorType === 'ADMIN'
                                  ? 'bg-indigo-100 text-indigo-800'
                                  : row.actorType === 'CLIENT'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {ACTOR_LABEL[row.actorType] ?? row.actorType}
                            </span>
                            <div className="mt-1 text-xs text-gray-600 break-all">{row.actorEmail ?? row.actorId ?? '—'}</div>
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-gray-900">{row.action}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium text-gray-900">{row.entityType}</div>
                            <div className="font-mono text-xs text-gray-600 break-all">{row.entityId}</div>
                          </td>
                          <td className="px-4 py-3 text-xs font-mono text-gray-700">{row.ipAddress}</td>
                          <td className="px-4 py-3 text-xs text-gray-700 max-w-md">
                            <PreviewText label="old" text={row.oldValue} />
                            <PreviewText label="new" text={row.newValue} />
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr>
                            <td colSpan={7} className="p-0">
                              <RowDetailPanel row={row} />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 bg-gray-50">
              <p className="text-sm text-gray-600">
                {totalCount === 0 ? (
                  <>
                    Total: <strong>0</strong> entries
                  </>
                ) : (
                  <>
                    Showing <strong>{Math.min((page - 1) * pageSize + 1, totalCount)}</strong>–
                    <strong>{(page - 1) * pageSize + rows.length}</strong> of <strong>{totalCount}</strong>
                  </>
                )}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
